import { watchSession, clearSession as clearPortalSession } from '../../shared/auth/portalSession';
import React, { useState } from 'react';
import Login from './components/Login';
import BookingFlow from './components/BookingFlow';
import Appointments from './components/Appointments';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import SessionWarning from './components/SessionWarning';
import type { SecretaryUser } from '../../shared/api/secretaryApi';

const App: React.FC = () => {
  const [user, setUser] = useState<SecretaryUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState<number>(0);
  const [currentView, setCurrentView] = useState<'booking' | 'appointments'>('appointments');

  // Check for existing session from localStorage and validate
  React.useEffect(() => watchSession<SecretaryUser>('secretary', session => {
    setUser(session?.user ?? null);
    setTokenExpiry(session ? session.expiresAt * 1000 : 0);
    setLoading(false);
  }), []);

  const clearSession = () => clearPortalSession('secretary');
  const handleLoginSuccess = (_user: SecretaryUser) => { /* saveSession notifies the watcher. */ };

  const handleLogout = () => {
    setUser(null);
    setTokenExpiry(0);
    clearSession();
  };

  const handleRefreshSession = () => {
    // Clear current session and show login
    setUser(null);
    setTokenExpiry(0);
    clearSession();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block mb-6">
            <div className="w-20 h-20 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">מרכז מונא הרפואי - תורים</h2>
          <p className="text-lg text-teal-600 font-medium">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!user ? (
        <Login onLoginSuccess={handleLoginSuccess} />
      ) : (
        <>
          <SessionWarning expiryTime={tokenExpiry} onRefresh={handleRefreshSession} />
          <Header 
            user={user} 
            onLogout={handleLogout}
            currentView={currentView}
            onNavigate={setCurrentView}
          />
          <div className="flex pt-20">
            <Sidebar 
              currentView={currentView}
              onNavigate={setCurrentView}
            />
            <div className="flex-1 md:mr-64 p-4 lg:p-6">
              {currentView === 'booking' ? (
                <BookingFlow user={user} />
              ) : (
                <Appointments user={user} />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default App;
